"use client";

import {
  ACHIEVEMENT_KEYS,
  ACHIEVEMENTS,
  AchievementKey,
  getAchievementChipClasses,
} from "@/lib/achievements";
import { useTournamentAchievements } from "@/hooks/useTournamentAchievements";
import { Tooltip } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslations } from "next-intl";

interface AchievementBadgesProps {
  tournamentId: string;
  teamId: string;
  size?: "sm" | "md";
  variant?: "row" | "list";
  className?: string;
}

/**
 * Renders the subset of the 8 tournament achievement icons that the given
 * team currently holds. Subscribes once per tournament via
 * useTournamentAchievements (single Firestore doc, snapshot-based) — there
 * is no per-team listener. Renders nothing when the team holds no titles.
 *
 * Designed to sit inside an existing container (e.g. the stats box footer)
 * — renders as a centered row of coloured icons, no backdrop of its own.
 */
export function AchievementBadges({
  tournamentId,
  teamId,
  size = "sm",
  variant = "row",
  className,
}: AchievementBadgesProps) {
  const tStats = useTranslations("tournamentStatistics.summary");
  const tTeams = useTranslations("teams");
  const { data } = useTournamentAchievements(tournamentId);

  if (!data?.holders) return null;

  const owned: AchievementKey[] = ACHIEVEMENT_KEYS.filter(
    (key) => data.holders[key]?.teamId === teamId,
  );

  if (owned.length === 0) return null;

  const iconSize = size === "sm" ? 16 : 20;

  if (variant === "list") {
    return (
      <div className={`flex flex-col gap-1.5${className ? ` ${className}` : ""}`}>
        <div className="flex items-center gap-2">
          <h4 className="text-xs font-medium text-amber-500/80 uppercase tracking-wider">
            {tTeams("achievements")}
          </h4>
          <div className="flex-1 h-px bg-amber-500/20" />
        </div>
        <ul
          className="flex flex-col gap-1"
          aria-label="Team achievements"
        >
        {owned.map((key) => {
          const meta = ACHIEVEMENTS[key];
          const chip = getAchievementChipClasses(key);
          const label = tStats(labelKey(key));
          return (
            <li
              key={key}
              className={`flex items-center gap-2.5 rounded-md px-2 py-1.5 ${chip.bg}`}
            >
              <span
                className={`inline-flex flex-none items-center justify-center ${chip.text}`}
                aria-hidden
              >
                <Icon icon={meta.icon} width={iconSize} height={iconSize} />
              </span>
              <span className="text-xs font-medium text-white/90 leading-tight">
                {label}
              </span>
            </li>
          );
        })}
        </ul>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-wrap items-center justify-center gap-1.5${className ? ` ${className}` : ""}`}
      aria-label="Team achievements"
    >
      {owned.map((key) => {
        const meta = ACHIEVEMENTS[key];
        const chip = getAchievementChipClasses(key);
        const label = tStats(labelKey(key));
        return (
          <Tooltip
            key={key}
            content={label}
            size="sm"
            placement="top"
            closeDelay={0}
            delay={200}
          >
            <span
              className={`inline-flex flex-none items-center justify-center ${chip.text}`}
              role="img"
              aria-label={label}
              tabIndex={0}
            >
              <Icon
                icon={meta.icon}
                width={iconSize}
                height={iconSize}
                aria-hidden
              />
            </span>
          </Tooltip>
        );
      })}
    </div>
  );
}

/**
 * tournamentStatistics.summary uses slightly different keys than the
 * achievement registry (e.g. `topBraveheartDecliner` → `braveheartDeclines`).
 */
function labelKey(key: AchievementKey): string {
  switch (key) {
    case "topBraveheartDecliner":
      return "braveheartDeclines";
    default:
      return key;
  }
}
